import type * as React from "react";
import { useCallback, useState } from "react";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import { Button } from "@/ui/atoms";
import { EmptyState, StatusBadge } from "@/ui/molecules";
import { cn } from "@/utils/utils";
import type { TAsyncState } from "@/store";
import type { TApiOriginalPreview } from "@/apis/proda_finance/types";
import { BUSINESS_TIMEZONE } from "@/modules/rev_apac/clock";
import { APAC_STATUS_LABELS } from "@/modules/rev_apac/statusLabels";
import { canDownloadFileArtifact } from "./canDownloadFileArtifact";
import { fileTypeLabel } from "./fileTypeLabel";
import { OriginalFilePreview } from "./OriginalFilePreview";
import type { TSubmissionContext, TSubmissionSlot } from "./submissionContext";

// The business clock, matching the rest of the page — the header labels the page UTC+8
// and Submission history renders the same instants on the same clock. Formatting
// this column in UTC made an upload near midnight read as a different calendar
// day depending on which tab you were looking at.
const UPLOADED_AT_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_TIMEZONE,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
});

/** Byte size as the list shows it. Null stays "—": absent is not zero. */
function formatSize(bytes: number | null): string {
    if (bytes === null) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatUploadedAt(value: string | null): string {
    if (value === null) return "—";
    return UPLOADED_AT_FORMATTER.format(new Date(value));
}

export type TOriginalFilesTabProps = {
    context: TAsyncState<TSubmissionContext>;
    /**
     * Whether this user may pull TB/GL/OTB file bytes. Preview shares the gate: the
     * preview endpoint enforces the same action, so offering one without the other
     * would only ever produce a 403.
     *
     * A submission's slots can mix file types — a deal-scoped FinJournal can sit
     * beside a TB on the same deal — and the backend gates each type separately
     * (`tbgl_file_download` vs. `finjournal_file_download`). So this is one of a
     * pair, picked per row by the row's own file type, never by the file that
     * happens to be open.
     */
    tbglCaps: { canDownload: boolean };
    /** Whether this user may pull FinJournal file bytes. See `tbglCaps`. */
    finjournalCaps: { canDownload: boolean };
    loadPreview: (fileId: string, sheet?: string) => Promise<TApiOriginalPreview>;
    onDownload: (fileId: string) => Promise<void>;
};

/**
 * The submission's uploaded originals for the selected period — one row per file
 * type, whether or not it was uploaded.
 *
 * Deliberately lists *every* type rather than only the open file's: the question
 * this view answers is "what did they actually send us this month", which is a
 * property of the submission, not of the file that happens to be open. This tab
 * is submission-scoped — the Review group's type selector picks which file is
 * open for review, but it does not narrow this list.
 *
 * Only the active file per type appears. Superseded, failed and rejected attempts
 * are the Submission History view's subject, reached from the same navigation group.
 */
export function OriginalFilesTab(props: TOriginalFilesTabProps): React.ReactElement {
    const { context, tbglCaps, finjournalCaps, loadPreview, onDownload } = props;
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const toggleRow = useCallback((fileId: string) => {
        setExpanded((previous) => {
            const next = new Set(previous);
            if (next.has(fileId)) next.delete(fileId);
            else next.add(fileId);
            return next;
        });
    }, []);

    if (context.status === "rejected") {
        return (
            <p data-testid="original-files-error" className="py-6 text-sm text-destructive">
                Could not load this submission&apos;s files.
            </p>
        );
    }

    if (context.data === null) {
        return (
            <div data-testid="original-files-loading" className="py-6">
                <div className="h-6 w-full animate-pulse rounded bg-muted" />
            </div>
        );
    }

    // Same rule the navigation uses: a type is listed if the deal expects it, or if
    // a file exists for it even though it was not expected.
    const slots: TSubmissionSlot[] = context.data.slots.filter(
        (slot) => slot.expected || slot.fileId !== null,
    );

    if (slots.length === 0) {
        return (
            <div data-testid="original-files-empty">
                <EmptyState title="No files for this period." />
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {slots.map((slot) => {
                const isExpanded = slot.fileId !== null && expanded.has(slot.fileId);
                // The same gate the Review and ETL tabs use, applied to this row's own
                // file type — not the open file's, since a submission's slots can mix.
                const canDownload = canDownloadFileArtifact(
                    { fileType: slot.fileType },
                    finjournalCaps,
                    tbglCaps,
                );
                return (
                    <div
                        key={slot.fileType}
                        data-testid={`original-files-row-${slot.fileType}`}
                        className="rounded-lg border border-border px-4 py-3"
                    >
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            <span className="w-12 shrink-0 text-sm font-semibold">
                                {fileTypeLabel(slot.fileType)}
                            </span>

                            {slot.fileId === null ? (
                                <span className="text-sm text-muted-foreground">Not uploaded</span>
                            ) : (
                                <>
                                    <span className="min-w-0 flex-1 truncate text-sm">
                                        {slot.originalFilename}
                                    </span>
                                    {slot.status !== null && (
                                        <StatusBadge
                                            status={slot.status}
                                            labels={APAC_STATUS_LABELS}
                                        />
                                    )}
                                    <span className="whitespace-nowrap text-sm text-muted-foreground">
                                        {slot.uploadedByName ?? "—"}
                                    </span>
                                    <span className="whitespace-nowrap text-sm text-muted-foreground">
                                        {formatUploadedAt(slot.uploadedAt)}
                                    </span>
                                    <span className="whitespace-nowrap text-sm text-muted-foreground">
                                        {formatSize(slot.fileSizeBytes)}
                                    </span>

                                    {canDownload && (
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                data-testid={`original-files-preview-toggle-${slot.fileType}`}
                                                aria-expanded={isExpanded}
                                                onClick={() => toggleRow(slot.fileId as string)}
                                            >
                                                {isExpanded ? (
                                                    <ChevronDown className="mr-1 h-4 w-4" />
                                                ) : (
                                                    <ChevronRight className="mr-1 h-4 w-4" />
                                                )}
                                                Preview
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                data-testid={`original-files-download-${slot.fileType}`}
                                                onClick={() =>
                                                    void onDownload(slot.fileId as string)
                                                }
                                            >
                                                <Download className="mr-1 h-4 w-4" />
                                                Download
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className={cn(!isExpanded && "hidden")}>
                            {isExpanded && slot.fileId !== null && (
                                <OriginalFilePreview
                                    fileId={slot.fileId}
                                    loadPreview={loadPreview}
                                />
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
